/**
 * SyncStatusPill — compact in-row status indicator (May 25, 2026).
 *
 * Renders one of four states for a given connection identifier:
 *
 *   - ``idle``        — no job ever / last job was completed > 30s ago
 *   - ``running``     — pending / processing / running (shows percent)
 *   - ``cancelling``  — cancel request in flight
 *   - ``failed``      — last terminal status was failure
 *
 * Subscribes to the unified ``SyncStore`` via ``useLatestJobFor`` so it
 * lights up whenever the user kicks off a sync from the filter sheet
 * (or the BE-rehydrate sees a job started elsewhere).
 *
 * Used inside ``ConnectedAccountRow`` — one pill per connection.
 */

import { Loader2, AlertCircle } from "lucide-react";
import type * as React from "react";
import { useLingui } from "@lingui/react/macro";

import { cn } from "@ando/ui";

import {
  type SyncSource,
  type SyncIdentifier,
  isJobActive,
  useLatestJobFor,
} from "@/features/sync/state/syncStore";

export interface SyncStatusPillProps {
  readonly source: SyncSource;
  /**
   * Predicate matching the connection — typically a function comparing
   * ``identifier.email_address`` or ``identifier.connection_key`` to
   * the row's connection.
   */
  readonly match: (id: SyncIdentifier) => boolean;
  readonly className?: string;
}

export const SyncStatusPill: React.FC<SyncStatusPillProps> = ({
  source,
  match,
  className,
}) => {
  const { t } = useLingui();
  const job = useLatestJobFor(source, match);
  if (!job) return null;

  const isActive = isJobActive(job.status);
  const isCancelling = job.status === "cancelling";
  const isFailed = job.status === "failed";

  if (job.status === "completed" || job.status === "cancelled") {
    // Terminal — let the toast own the surface. The pill goes quiet.
    return null;
  }

  // June 4 2026 — V1 audit G5. Surface the per-item counter ("X/Y file ·
  // Z%") whenever the BE has emitted a ``total_items`` > 0. Same
  // information the V1 ``ConnectionRow.tsx:274-307`` progress bar
  // exposed; presented here as inline pill text so the row layout stays
  // single-line. Falls back to "Sync Z%" when the count isn't known yet
  // (first event window) and to bare "Sync" when not even progress is
  // available.
  const active = isActive
    ? job.totalItems > 0
      ? t`Sync ${job.completedItems.toLocaleString("it-IT")}/${job.totalItems.toLocaleString("it-IT")} · ${job.progress}%`
      : job.progress > 0
        ? t`Sync ${job.progress}%`
        : t`Sync`
    : null;
  // ``aria-label`` numbers go through the same Italian locale formatter
  // as the visible text so screen readers and sighted users hear/see the
  // same separator (12.345 vs "twelve thousand three hundred forty-five").
  const ariaLabel = isFailed
    ? t`Sincronizzazione fallita`
    : isCancelling
      ? t`Annullamento sincronizzazione in corso`
      : active && job.totalItems > 0
        ? t`Sincronizzazione in corso, ${job.completedItems.toLocaleString("it-IT")} di ${job.totalItems.toLocaleString("it-IT")} elementi, ${job.progress}%`
        : t`Sincronizzazione in corso`;
  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium text-[10.5px] tabular-nums",
        isFailed
          ? "bg-error/15 text-error"
          : isCancelling
            ? "bg-ink-3/15 text-ink-3"
            : "bg-ando-orange-soft text-ando-orange",
        className,
      )}
    >
      {isFailed ? (
        <AlertCircle className="h-3 w-3" strokeWidth={2} />
      ) : (
        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
      )}
      {isFailed
        ? t`Errore sync`
        : isCancelling
          ? t`Annullando…`
          : (active ?? t`Sync`)}
    </span>
  );
};
SyncStatusPill.displayName = "SyncStatusPill";
